import type { Router } from 'vue-router';

/**
 * A tab opened before a deploy still asks for the old build's code files (hashed names), which the server no longer
 * has: opening a page then fails ("Failed to fetch dynamically imported module"). Reload once, straight to the page
 * the person was opening, so the new build loads. A reload within the last 30 s is not repeated (no loops if the
 * server itself is broken).
 */
const KEY = 'codot-stale-build-reload';

export function isStaleChunkError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(msg);
}

export function reloadForNewBuild(url: string): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (Date.now() - last < 30_000) return false;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    /* storage blocked: reload anyway, the browser's own history guards against a tight loop */
  }
  window.location.assign(url);
  return true;
}

export function installStaleBuildRecovery(router: Router): void {
  // Vite's own signal when a preloaded file of a lazy page fails.
  window.addEventListener('vite:preloadError', (e) => {
    if (reloadForNewBuild(window.location.href)) e.preventDefault();
  });
  router.onError((err, to) => {
    if (isStaleChunkError(err)) reloadForNewBuild(to.fullPath);
  });
}
